import {
    createContext,
    FormEvent,
    ReactNode,
    useCallback,
    useContext,
    useRef,
    useState,
    useEffect,
    useSyncExternalStore,
} from 'react';
import {useLocation} from 'react-router-dom';
import {userStore} from '../../../utilities/common/userStore';
import type {SelfData} from '../../../shared/constants/user';
import {getInsightsChatApiUrl} from '../../../shared/service/insightsChat';
import Network from '../../../shared/service/network';

export type ToolVisualizationArgs = Record<string, unknown>;

export type ChatMessage = {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    visualizations?: ToolVisualizationArgs[];
};

export type ChatConversation = {
    id: string;
    userId: string;
    organisationId: number;
    title: string;
    messages: ChatMessage[];
    createdAt: string;
    updatedAt: string;
};

type ChatScope = {
    userId: string;
    organisationId: number;
};

type AIChatContextValue = {
    conversationId: string;
    conversations: ChatConversation[];
    historyReady: boolean;
    scopeError: string | null;
    openConversation: (id: string) => void;
    deleteConversation: (id: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    prompt: string;
    setPrompt: (prompt: string) => void;
    messages: ChatMessage[];
    status: string;
    error: Error | undefined;
    stop: () => void;
    handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
    sendPrompt: (text: string) => Promise<void>;
    isDashboardPage: boolean;
    isGenerating: boolean;
    streamingAssistantMessageId: string | undefined;
    toolVisualizations: ToolVisualizationArgs[];
    clearChatForCurrentOrg: () => void;
};

const AIChatContext = createContext<AIChatContextValue | null>(null);

export const useAIChat = () => {
    const context = useContext(AIChatContext);
    if (!context) throw new Error('useAIChat must be used within AIChatProvider');
    return context;
};

let msgCounter = 0;
const newId = () => `msg-${++msgCounter}-${Date.now()}`;

export function AIChatProvider({children}: { children: ReactNode }) {
    const {pathname} = useLocation();
    const user = useSyncExternalStore(
        userStore.subscribe,
        userStore.get,
    ) as SelfData | null;

    const userId = user?.id;
    const [conversations, setConversations] =
        useState<ChatConversation[]>([]);

    const [historyReady, setHistoryReady] = useState(false);
    const [chatScope, setChatScope] = useState<ChatScope | null>(null);
    const [scopeError, setScopeError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [conversationId, setConversationId] = useState<string>(
        () => crypto.randomUUID(),
    );
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<Error | undefined>();
    const [toolVisualizations, setToolVisualizations] = useState<ToolVisualizationArgs[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);
    const streamingIdRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        streamingIdRef.current = null;

        setChatScope(null);
        setScopeError(null);
        setHistoryReady(false);
        setConversations([]);
        setMessages([]);
        setConversationId(crypto.randomUUID());
        setToolVisualizations([]);
        setPrompt('');
        setError(undefined);
        setIsGenerating(false);

        if (!userId) return;

        const loadChatScope = async () => {
            try {
                const result = await Network.client.get(
                    getInsightsChatApiUrl().replace(/\/chat$/, '/context'),
                );

                // Supports either an Axios response or an unwrapped response body.
                const scope = (result.data ?? result) as ChatScope;

                if (
                    String(scope.userId) !== String(userId) ||
                    !Number.isSafeInteger(scope.organisationId) ||
                    scope.organisationId <= 0
                ) {
                    throw new Error('Invalid chat context.');
                }

                if (!cancelled) {
                    setChatScope({
                        userId: String(scope.userId),
                        organisationId: scope.organisationId,
                    });
                }
            } catch {
                if (!cancelled) {
                    setScopeError('Unable to load your chat context. Please retry.');
                }
            }
        };

        void loadChatScope();

        return () => {
            cancelled = true;
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
            streamingIdRef.current = null;
        };
    }, [userId]);

    // Local storage keys and limits for AI chat history
    const historyPrefix =
        chatScope && chatScope.userId === String(userId)
            ? `ai_chat_v2:${chatScope.userId}:${chatScope.organisationId}:`
            : null;

    const [loadedHistoryPrefix, setLoadedHistoryPrefix] =
        useState<string | null>(null);

    const isDashboardPage = pathname.startsWith('/d/');

    const stop = useCallback(() => {
        abortControllerRef.current?.abort();
        setIsGenerating(false);
    }, []);

    const clearChatForCurrentOrg = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        streamingIdRef.current = null;

        setConversationId(crypto.randomUUID());
        setMessages([]);
        setPrompt('');
        setToolVisualizations([]);
        setError(undefined);
        setIsGenerating(false);
    }, []);

    const openConversation = useCallback((id: string) => {
        if (
            !historyReady ||
            !chatScope ||
            !historyPrefix ||
            loadedHistoryPrefix !== historyPrefix
        ) {
            return;
        }

        const conversation = conversations.find(
            (item) =>
                item.id === id &&
                item.userId === chatScope.userId &&
                item.organisationId === chatScope.organisationId,
        );

        if (!conversation) {
            setError(new Error('Conversation not found.'));
            return;
        }

        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        streamingIdRef.current = null;

        setConversationId(conversation.id);
        setMessages(conversation.messages);
        setPrompt('');
        setError(undefined);
        setIsGenerating(false);

        // Support the existing graph display until the panel is updated.
        const lastAssistant = [...conversation.messages]
            .reverse()
            .find((message) => message.role === 'assistant');

        setToolVisualizations(lastAssistant?.visualizations ?? []);
    }, [
        conversations,
        historyReady,
        chatScope,
        historyPrefix,
        loadedHistoryPrefix,
    ]);

    const deleteConversation = useCallback(
        (id: string) => {
            if (
                !historyReady ||
                !historyPrefix ||
                loadedHistoryPrefix !== historyPrefix
            ) {
                return;
            }

            const conversation = conversations.find(
                (item) => item.id === id,
            );

            if (!conversation) return;

            localStorage.removeItem(`${historyPrefix}${id}`);

            setConversations((current) =>
                current.filter((item) => item.id !== id),
            );

            // If the open conversation is deleted, start a fresh chat.
            if (id === conversationId) {
                abortControllerRef.current?.abort();
                abortControllerRef.current = null;
                streamingIdRef.current = null;

                setConversationId(crypto.randomUUID());
                setMessages([]);
                setPrompt('');
                setToolVisualizations([]);
                setError(undefined);
                setIsGenerating(false);
            }
        },
        [
            conversations,
            conversationId,
            historyReady,
            historyPrefix,
            loadedHistoryPrefix,
        ],
    );

    const sendPrompt = useCallback(async (text: string) => {
        const trimmedPrompt = text.trim();
        if (
            !trimmedPrompt ||
            isGenerating ||
            !historyReady ||
            !historyPrefix ||
            loadedHistoryPrefix !== historyPrefix
        ) {
            return;
        }

        setPrompt('');
        setError(undefined);
        setToolVisualizations([]); // clear previous visualizations

        // Add user message
        const userMsg: ChatMessage = {id: newId(), role: 'user', content: trimmedPrompt};
        setMessages((prev) => [...prev, userMsg]);

        // Add empty assistant message
        const assistantId = newId();
        streamingIdRef.current = assistantId;
        setMessages((prev) => [...prev, {id: assistantId, role: 'assistant', content: ''}]);
        setIsGenerating(true);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const allMessages = [...messages, userMsg];

            const stream = (await Network.client.post(
                getInsightsChatApiUrl(),
                {
                    conversationId,
                    messages: allMessages.map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                    organisationId: localStorage.getItem('org_id') || '',
                },
                {
                    adapter: 'fetch',
                    responseType: 'stream',
                    signal: abortController.signal,
                },
            )) as unknown as ReadableStream<Uint8Array>;

            if (!stream) {
                throw new Error('No response stream');
            }

            const reader = stream.getReader();
            const decoder = new TextDecoder();
            let pending = '';

            while (true) {
                const {done, value} = await reader.read();

                if (
                    abortController.signal.aborted ||
                    abortControllerRef.current !== abortController
                ) {
                    break;
                }

                pending += done
                    ? decoder.decode()
                    : decoder.decode(value, {stream: true});

                const lines = pending.split('\n');
                pending = lines.pop() ?? '';

                if (done && pending.trim()) {
                    lines.push(pending);
                    pending = '';
                }

                for (const line of lines) {
                    const trimmed = line.trim();

                    // Text response.
                    if (trimmed.startsWith('0:')) {
                        try {
                            const text = JSON.parse(trimmed.slice(2)) as string;

                            setMessages((prev) =>
                                prev.map((message) =>
                                    message.id === assistantId
                                        ? {...message, content: message.content + text}
                                        : message,
                                ),
                            );
                        } catch {
                            // Ignore malformed text events.
                        }
                    }

                    // Graph data.
                    if (trimmed.startsWith('9:')) {
                        try {
                            const parsed = JSON.parse(trimmed.slice(2)) as {
                                toolName?: string;
                                args?: Record<string, unknown>;
                            };

                            if (
                                parsed.toolName === 'render_visualisation' &&
                                parsed.args
                            ) {
                                const visualization = parsed.args;

                                setMessages((prev) =>
                                    prev.map((message) =>
                                        message.id === assistantId
                                            ? {
                                                ...message,
                                                visualizations: [
                                                    ...(message.visualizations ?? []),
                                                    visualization,
                                                ],
                                            }
                                            : message,
                                    ),
                                );

                                setToolVisualizations((prev) => [
                                    ...prev,
                                    visualization,
                                ]);
                            }
                        } catch {
                            // Ignore malformed tool events.
                        }
                    }
                }

                if (done) break;
            }
        } catch (err: unknown) {
            if (!abortController.signal.aborted) {
                setError(err instanceof Error ? err : new Error('Unknown error'));
            }
        } finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null;
                setIsGenerating(false);
                streamingIdRef.current = null;
            }
        }
    }, [
        isGenerating,
        messages,
        conversationId,
        historyReady,
        historyPrefix,
        loadedHistoryPrefix,
    ]);

    // Load history on mount for current organisation (if messages empty)
    // Load the saved conversation list for this user and organisation.
    useEffect(() => {
        setHistoryReady(false);
        setLoadedHistoryPrefix(null);
        setConversations([]);

        if (!historyPrefix || !chatScope) return;

        try {
            const saved: ChatConversation[] = [];
            const uuidV4 =
                /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

            for (let index = 0; index < localStorage.length; index++) {
                const key = localStorage.key(index);

                if (!key?.startsWith(historyPrefix)) continue;

                try {
                    const conversation = JSON.parse(
                        localStorage.getItem(key) || 'null',
                    ) as ChatConversation | null;

                    if (
                        !conversation ||
                        typeof conversation.id !== 'string' ||
                        !uuidV4.test(conversation.id) ||
                        key !== `${historyPrefix}${conversation.id}` ||
                        conversation.userId !== chatScope.userId ||
                        conversation.organisationId !== chatScope.organisationId ||
                        typeof conversation.title !== 'string' ||
                        typeof conversation.createdAt !== 'string' ||
                        typeof conversation.updatedAt !== 'string' ||
                        !Array.isArray(conversation.messages) ||
                        !conversation.messages.every(
                            (message) =>
                                message &&
                                typeof message.id === 'string' &&
                                ['user', 'assistant'].includes(message.role) &&
                                typeof message.content === 'string' &&
                                (message.visualizations === undefined ||
                                    Array.isArray(message.visualizations)),
                        )
                    ) {
                        continue;
                    }

                    saved.push(conversation);
                } catch {
                    // Skip an invalid entry without deleting it.
                }
            }

            saved.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

            setConversations(saved);
            setLoadedHistoryPrefix(historyPrefix);
            setHistoryReady(true);
        } catch {
            setError(new Error('Unable to load saved conversations.'));
        }
    }, [historyPrefix, chatScope]);

    // Save each conversation separately, including its graphs.
    useEffect(() => {
        if (
            !historyReady ||
            !historyPrefix ||
            loadedHistoryPrefix !== historyPrefix ||
            !chatScope ||
            messages.length === 0
        ) {
            return;
        }

        try {
            const key = `${historyPrefix}${conversationId}`;
            const previous = JSON.parse(
                localStorage.getItem(key) || 'null',
            ) as ChatConversation | null;

            const now = new Date().toISOString();

            const conversation: ChatConversation = {
                id: conversationId,
                userId: chatScope.userId,
                organisationId: chatScope.organisationId,
                title:
                    messages
                        .find((message) => message.role === 'user')
                        ?.content.trim()
                        .slice(0, 80) || 'New conversation',
                messages,
                createdAt: previous?.createdAt ?? now,
                updatedAt: now,
            };

            localStorage.setItem(key, JSON.stringify(conversation));

            setConversations((current) => [
                conversation,
                ...current.filter((item) => item.id !== conversationId),
            ]);
        } catch {
            setError(
                new Error(
                    'Unable to save this conversation. Browser storage may be full.',
                ),
            );
        }
    }, [
        messages,
        conversationId,
        historyReady,
        historyPrefix,
        loadedHistoryPrefix,
        chatScope,
    ]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await sendPrompt(prompt);
    };

    const streamingAssistantMessageId = streamingIdRef.current ?? undefined;
    const status = isGenerating ? 'streaming' : 'awaiting-message';

    const value: AIChatContextValue = {
        conversationId,
        conversations,
        historyReady,
        scopeError,
        openConversation,
        deleteConversation,
        open, setOpen,
        prompt, setPrompt,
        messages,
        status,
        error,
        stop,
        handleSubmit,
        sendPrompt,
        isDashboardPage,
        isGenerating,
        streamingAssistantMessageId,
        toolVisualizations,
        clearChatForCurrentOrg,
    };

    return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
}